import React from 'react';
import { useCRM } from '../context/CRMContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, UsersIcon, UserCheckIcon, UserXIcon, DollarSignIcon } from 'lucide-react';
export function Funnel() {
  const {
    selectedCompany
  } = useData();
  const {
    getContactsByCompanyId
  } = useCRM();
  const contacts = selectedCompany ? getContactsByCompanyId(selectedCompany.id) : [];
  // Calculate funnel metrics
  const leads = contacts.filter(c => c.status === 'lead');
  const contactStatus = contacts.filter(c => c.status === 'contact');
  const customers = contacts.filter(c => c.status === 'customer');
  const lost = contacts.filter(c => c.status === 'lost');
  const totalRevenue = customers.reduce((sum, customer) => {
    return sum + customer.companies.reduce((companySum, rel) => companySum + rel.lifetime_purchase, 0);
  }, 0);
  const avgDealSize = customers.length > 0 ? totalRevenue / customers.length : 0;
  // Conversion rates
  const leadToContactRate = leads.length > 0 ? contactStatus.length / (leads.length + contactStatus.length) * 100 : 0;
  const contactToCustomerRate = contactStatus.length > 0 ? customers.length / (contactStatus.length + customers.length) * 100 : 0;
  const overallConversionRate = leads.length + contactStatus.length > 0 ? customers.length / (leads.length + contactStatus.length + customers.length) * 100 : 0;
  const funnelStages = [{
    name: 'Leads',
    count: leads.length,
    percentage: 100,
    color: 'bg-yellow-500',
    icon: UsersIcon
  }, {
    name: 'Contacts',
    count: contactStatus.length,
    percentage: leads.length > 0 ? contactStatus.length / leads.length * 100 : 0,
    color: 'bg-blue-500',
    icon: TrendingUpIcon
  }, {
    name: 'Customers',
    count: customers.length,
    percentage: contactStatus.length > 0 ? customers.length / contactStatus.length * 100 : 0,
    color: 'bg-green-500',
    icon: UserCheckIcon
  }];
  return <div className="space-y-6" data-id="element-1051">
      <div data-id="element-1052">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-1053">
          Sales Funnel
        </h1>
        <p className="text-gray-600" data-id="element-1054">
          Track your pipeline and conversion rates
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1055">
        <Card data-id="element-1056">
          <div className="flex items-center justify-between" data-id="element-1057">
            <div data-id="element-1058">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1059">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1060">
                {contacts.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-1061">
              <UsersIcon className="w-6 h-6 text-gray-600" data-id="element-1062" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1063">
          <div className="flex items-center justify-between" data-id="element-1064">
            <div data-id="element-1065">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1066">Customers</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-1067">
                {customers.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-1068">
              <UserCheckIcon className="w-6 h-6 text-green-600" data-id="element-1069" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1070">
          <div className="flex items-center justify-between" data-id="element-1071">
            <div data-id="element-1072">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1073">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1074">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1075">
              <DollarSignIcon className="w-6 h-6 text-blue-600" data-id="element-1076" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1077">
          <div className="flex items-center justify-between" data-id="element-1078">
            <div data-id="element-1079">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1080">Avg Deal Size</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1081">
                {formatCurrency(avgDealSize)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-1082">
              <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-1083" />
            </div>
          </div>
        </Card>
      </div>

      {/* Funnel Visualization */}
      <Card data-id="element-1084">
        <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1085">
          Pipeline Stages
        </h2>
        <div className="space-y-6" data-id="element-1086">
          {funnelStages.map((stage, index) => {
          const Icon = stage.icon;
          return <div key={stage.name} data-id="element-1087">
                <div className="flex items-center justify-between mb-2" data-id="element-1088">
                  <div className="flex items-center gap-3" data-id="element-1089">
                    <div className={`w-10 h-10 ${stage.color.replace('bg-', 'bg-').replace('-500', '-100')} rounded-lg flex items-center justify-center`} data-id="element-1090">
                      <Icon className={`w-5 h-5 ${stage.color.replace('bg-', 'text-')}`} data-id="element-1091" />
                    </div>
                    <div data-id="element-1092">
                      <h3 className="text-sm font-semibold text-gray-900" data-id="element-1093">
                        {stage.name}
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-1094">
                        {stage.count} contacts
                        {index > 0 && ` • ${stage.percentage.toFixed(1)}% conversion`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1095">
                    <p className="text-lg font-bold text-gray-900" data-id="element-1096">
                      {stage.count}
                    </p>
                  </div>
                </div>
                <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden" data-id="element-1097">
                  <div className={`absolute inset-y-0 left-0 ${stage.color} transition-all duration-500 flex items-center justify-center`} style={{
                width: `${Math.max(stage.percentage, 5)}%`
              }} data-id="element-1098">
                    <span className="text-white text-sm font-semibold" data-id="element-1099">
                      {stage.percentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </Card>

      {/* Conversion Rates & Lost */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1100">
        <Card data-id="element-1101">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1102">
            Conversion Rates
          </h2>
          <div className="space-y-4" data-id="element-1103">
            <div data-id="element-1104">
              <div className="flex items-center justify-between mb-2" data-id="element-1105">
                <span className="text-sm text-gray-600" data-id="element-1106">Lead → Contact</span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1107">
                  {leadToContactRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1108">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{
                width: `${leadToContactRate}%`
              }} data-id="element-1109" />
              </div>
            </div>
            <div data-id="element-1110">
              <div className="flex items-center justify-between mb-2" data-id="element-1111">
                <span className="text-sm text-gray-600" data-id="element-1112">
                  Contact → Customer
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1113">
                  {contactToCustomerRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1114">
                <div className="h-full bg-green-500 transition-all duration-500" style={{
                width: `${contactToCustomerRate}%`
              }} data-id="element-1115" />
              </div>
            </div>
            <div data-id="element-1116">
              <div className="flex items-center justify-between mb-2" data-id="element-1117">
                <span className="text-sm text-gray-600" data-id="element-1118">
                  Overall Conversion
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1119">
                  {overallConversionRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1120">
                <div className="h-full bg-purple-500 transition-all duration-500" style={{
                width: `${overallConversionRate}%`
              }} data-id="element-1121" />
              </div>
            </div>
          </div>
        </Card>

        <Card data-id="element-1122">
          <div className="flex items-center justify-between mb-4" data-id="element-1123">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-1124">
              Lost Opportunities
            </h2>
            <div className="flex items-center gap-2" data-id="element-1125">
              <UserXIcon className="w-5 h-5 text-gray-400" data-id="element-1126" />
              <span className="text-2xl font-bold text-gray-900" data-id="element-1127">
                {lost.length}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1128">
            {lost.length} contacts marked as lost
          </p>
          {lost.length > 0 && <div className="space-y-2" data-id="element-1129">
              <p className="text-xs font-semibold text-gray-500 uppercase" data-id="element-1130">
                Recent Lost Contacts
              </p>
              {lost.slice(0, 5).map(contact => <div key={contact.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-1131">
                  <div data-id="element-1132">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1133">
                      {contact.first_name} {contact.last_name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1134">{contact.company}</p>
                  </div>
                </div>)}
            </div>}
        </Card>
      </div>
    </div>;
}